import React, { ChangeEvent, InputHTMLAttributes, useEffect, useRef, useState } from 'react';
import InvalidFeedback from '../InvalidFeedback';
import { FormControl as Input, InlineWrapper, RemoveButton, Value, Wrapper } from './styles';
import Icon from 'components/atoms/Icon';
import { useTheme } from '@emotion/react';

export interface FormControlProps<T extends string | number = string> extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
    value?: T;
    onChange?: (value: T) => void;
    error?: string;
    notification?: string;
    isSmall?: boolean;
    className?: string;
    icon?: string;
    onIconClick?: () => void;
}

const FormHex = <T extends string | number>({
    error,
    notification,
    value: initialValue,
    onChange,
    onBlur,
    onFocus,
    onIconClick,
    icon,
    ...props
}: FormControlProps<T>) => {
    const [value, setValue] = useState<string>(initialValue?.toString() ?? '');
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const { colors } = useTheme();
    const ref = useRef<HTMLInputElement>(null);

    // Life cycle.
    useEffect(() => {
        if (initialValue === value) {
            return;
        }

        setValue(initialValue?.toString() ?? '');
    }, [initialValue]); // do not add `value` as dependency!

    // Methods.
    const handleOnFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true);

        if (onFocus) {
            onFocus(event);
        }
    };

    const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(false);

        if (onBlur) {
            onBlur(event);
        }
    };

    const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        handleValueChange(value);
    };

    const handleValueChange = (value: string) => {
        setValue(value);
        onChange?.(value as T);
    };

    const handleOnIconClick = () => {
        if (onIconClick) {
            onIconClick();
        } else {
            ref.current?.focus();
        }
    };

    return (
        <>
            <Wrapper isFocused={isFocused} isSmall={props.isSmall} notification={!!notification} error={!!error}>
                <Input ref={ref} type="color" {...props} value={value} onBlur={handleOnBlur} onFocus={handleOnFocus} onChange={handleOnChange} />
                <Value>{value.toUpperCase()}</Value>
                {value !== '' &&
                    <RemoveButton onClick={() => handleValueChange('')}>✕</RemoveButton>
                }
                {(icon) && (
                    <InlineWrapper isSmall={props.isSmall} onClick={handleOnIconClick}>
                        <Icon name={icon} size={1} color={colors.dark} />
                    </InlineWrapper>
                )}
                {(error || notification) &&
                    <InlineWrapper isSmall={props.isSmall}>
                        <Icon name={error ? 'error' : 'warning'} size={1} color={error ? colors.red : colors.warning} />
                    </InlineWrapper>
                }
            </Wrapper>
            {error && <InvalidFeedback type="error">{error}</InvalidFeedback>}
            {notification && !error && <InvalidFeedback type="notification">{notification}</InvalidFeedback>}
        </>
    );
};

export default FormHex;

